/** 把浏览器麦克风转换成 Gateway 需要的单声道 PCM16 流。 */

import { encodePcm16, StreamingResampler } from "./audio";
import { createVoiceEventId } from "./protocol";

const WORKLET_SOURCE = `
class GoNovusMicrophoneProcessor extends AudioWorkletProcessor {
  process(inputs, outputs) {
    const input = inputs[0]?.[0];
    if (input && input.length) this.port.postMessage(input.slice());
    for (const channel of outputs[0] || []) channel.fill(0);
    return true;
  }
}
registerProcessor("go-novus-microphone", GoNovusMicrophoneProcessor);
`;

export interface MicrophoneOptions {
  onSamples: (samples: Float32Array, sampleRate: number) => void;
}

interface CaptureRun {
  stream?: MediaStream;
  source?: MediaStreamAudioSourceNode;
  worklet?: AudioWorkletNode;
}

function releaseRun(run: CaptureRun): void {
  const source = run.source;
  run.source = undefined;
  source?.disconnect();
  const worklet = run.worklet;
  run.worklet = undefined;
  worklet?.disconnect();
  worklet?.port.close();
  const stream = run.stream;
  run.stream = undefined;
  stream?.getTracks().forEach((track) => {
    track.stop();
  });
}

export class MicrophoneCapture {
  private current: CaptureRun | undefined;

  async start(
    context: AudioContext,
    options: MicrophoneOptions,
  ): Promise<void> {
    this.stop();
    const mediaDevices = (
      navigator as unknown as {
        mediaDevices?: { getUserMedia: MediaDevices["getUserMedia"] };
      }
    ).mediaDevices;
    const audioWorklet = (context as unknown as { audioWorklet?: AudioWorklet })
      .audioWorklet;
    if (!mediaDevices?.getUserMedia)
      throw new Error("This browser cannot access a microphone.");
    if (!audioWorklet)
      throw new Error(
        "This browser does not support microphone audio worklets.",
      );
    const run: CaptureRun = {};
    this.current = run;
    try {
      const stream = await mediaDevices.getUserMedia({
        audio: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      run.stream = stream;
      if (this.current !== run) {
        releaseRun(run);
        return;
      }
      const blob = new Blob([WORKLET_SOURCE], { type: "text/javascript" });
      const moduleUrl = URL.createObjectURL(blob);
      try {
        await audioWorklet.addModule(moduleUrl);
      } finally {
        URL.revokeObjectURL(moduleUrl);
      }
      if (this.current !== run) {
        releaseRun(run);
        return;
      }
      run.source = context.createMediaStreamSource(stream);
      run.worklet = new AudioWorkletNode(context, "go-novus-microphone", {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [1],
      });
      run.worklet.port.addEventListener(
        "message",
        (event: MessageEvent<unknown>) => {
          if (this.current !== run || !(event.data instanceof Float32Array))
            return;
          options.onSamples(event.data, context.sampleRate);
        },
      );
      run.worklet.port.start();
      run.source.connect(run.worklet);
      run.worklet.connect(context.destination);
    } catch (error) {
      releaseRun(run);
      if (this.current !== run) return;
      this.current = undefined;
      throw error;
    }
  }

  stop(): void {
    const run = this.current;
    this.current = undefined;
    if (run !== undefined) releaseRun(run);
  }
}

export class MicrophoneAudioSender {
  private readonly resampler = new StreamingResampler();
  private congested = false;

  reset(): void {
    this.resampler.reset();
    this.congested = false;
  }

  send(
    samples: Float32Array,
    sourceRate: number,
    targetRate: number,
    socket: WebSocket | undefined,
  ): void {
    if (!socket || socket.readyState !== WebSocket.OPEN || targetRate <= 0) {
      this.reset();
      return;
    }
    const buffered = socket.bufferedAmount;
    if (this.congested && buffered > 16 * 1024) return;
    if (buffered > 64 * 1024) {
      this.congested = true;
      return;
    }
    this.congested = false;
    const output = this.resampler.process(samples, sourceRate, targetRate);
    if (output.length === 0) return;
    try {
      socket.send(
        JSON.stringify({
          type: "input_audio_buffer.append",
          event_id: createVoiceEventId(),
          audio: encodePcm16(output),
        }),
      );
    } catch {
      this.reset();
    }
  }
}
